import React, { useState } from 'react';

const Contact: React.FC = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [heardAbout, setHeardAbout] = useState('');
  const [showThanks, setShowThanks] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [website, setWebsite] = useState('');

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    setError('');
    try {
      const response = await fetch('/api/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'contact',
          firstName,
          lastName,
          email,
          phone,
          heardAbout,
          message,
          website,
        }),
      });
      const result = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(typeof result?.error === 'string' ? result.error : 'Unable to submit your message. Please try again.');
      }
      setShowThanks(true);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Unable to submit your message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl rounded-2xl bg-white p-4 shadow sm:p-6">
      <h3 className="text-xl font-semibold mb-4">Contact Us</h3>
      <form onSubmit={submit} className="relative space-y-4">
        <div className="absolute -left-[9999px]" aria-hidden="true">
          <label htmlFor="contact-website">Leave this field empty</label>
          <input
            id="contact-website"
            name="website"
            type="text"
            autoComplete="off"
            tabIndex={-1}
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="contact-first-name" className="block text-sm font-medium text-gray-700">First Name *</label>
            <input id="contact-first-name" name="firstName" autoComplete="given-name" required maxLength={100} value={firstName} onChange={(e) => setFirstName(e.target.value)} className="mt-1 block min-h-[44px] w-full rounded border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-mainBlue" />
          </div>
          <div>
            <label htmlFor="contact-last-name" className="block text-sm font-medium text-gray-700">Last Name</label>
            <input id="contact-last-name" name="lastName" autoComplete="family-name" maxLength={100} value={lastName} onChange={(e) => setLastName(e.target.value)} className="mt-1 block min-h-[44px] w-full rounded border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-mainBlue" />
          </div>
        </div>

        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700">Email *</label>
          <input id="contact-email" name="email" autoComplete="email" required maxLength={254} value={email} onChange={(e) => setEmail(e.target.value)} type="email" className="mt-1 block min-h-[44px] w-full rounded border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-mainBlue" />
        </div>

        <div>
          <label htmlFor="contact-phone" className="block text-sm font-medium text-gray-700">Phone Number *</label>
          <input id="contact-phone" name="phone" autoComplete="tel" required maxLength={40} value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" className="mt-1 block min-h-[44px] w-full rounded border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-mainBlue" />
        </div>

        <div>
          <label htmlFor="contact-heard-about" className="block text-sm font-medium text-gray-700">How did you hear about us?</label>
          <select id="contact-heard-about" name="heardAbout" value={heardAbout} onChange={(e) => setHeardAbout(e.target.value)} className="mt-1 block min-h-[44px] w-full rounded border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-mainBlue">
            <option value="">Select an option (optional)</option>
            <option value="instagram">Instagram</option>
            <option value="friend-or-referral">Friend or referral</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div>
          <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700">Message</label>
          <textarea id="contact-message" name="message" maxLength={3000} value={message} onChange={(e) => setMessage(e.target.value)} rows={4} className="mt-1 block w-full rounded border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-mainBlue" />
        </div>

        <div className="text-right">
          <button
            type="submit"
            disabled={sending}
            className="min-h-[44px] rounded bg-mainPink px-6 py-2 font-semibold text-white transition-colors hover:bg-mainBlue disabled:cursor-not-allowed disabled:opacity-60"
          >
            {sending ? 'Sending…' : 'Submit'}
          </button>
        </div>
        {error && (
          <p role="alert" className="text-red-600 text-sm">
            {error}
          </p>
        )}
      </form>

      {showThanks && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="mx-4 max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl" role="dialog" aria-modal="true" aria-labelledby="contact-thanks-title">
            <h4 id="contact-thanks-title" className="text-lg font-semibold mb-2">Thank you</h4>
            <p className="text-sm text-gray-700 mb-4">
              Your message was received — we&apos;ll be in touch.
            </p>
            <div className="flex justify-center">
              <button onClick={() => setShowThanks(false)} className="min-h-[44px] rounded bg-mainBlue px-6 py-2 font-semibold text-white">OK</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contact;
